import { useEffect, useRef, useState } from 'react';
import { ConnectionLost } from '@/components/shared/ConnectionLost';
import { onNetworkError, onNetworkSuccess } from '@/api/client';
import { checkHealth } from '@/api/health';

const FAILURE_THRESHOLD = 3;
const POLL_INTERVAL_MS = 10000;

export function ConnectionWatcher(): JSX.Element {
  const [, setFailures] = useState(0);
  const [open, setOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const retryingRef = useRef(false);

  useEffect(() => {
    const unsubErr = onNetworkError(() => {
      setFailures((prev) => {
        const next = prev + 1;
        if (next >= FAILURE_THRESHOLD) setOpen(true);
        return next;
      });
    });
    const unsubOk = onNetworkSuccess(() => {
      setFailures(0);
      setOpen(false);
    });
    return () => {
      unsubErr();
      unsubOk();
    };
  }, []);

  const probe = async () => {
    if (retryingRef.current) return;
    retryingRef.current = true;
    setRetrying(true);
    try {
      const ok = await checkHealth();
      if (ok) {
        // Success handler in apiFetch fires only for requests through that
        // wrapper, so reset state manually here too.
        setFailures(0);
        setOpen(false);
      }
      // On !ok leave the overlay open; the next poll will retry.
    } finally {
      retryingRef.current = false;
      setRetrying(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => {
      void probe();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [open]);

  return (
    <ConnectionLost
      open={open}
      retryNowEnabled={!retrying}
      onRetryNow={() => void probe()}
    />
  );
}
